/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CalendarOutlined } from '@ant-design/icons'
import { DatePicker as AntDatePicker } from 'antd'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import React, { useEffect, useRef } from 'react'

import colors from '@sportninja/common/constants/appColors'
import { font } from 'src/components/css'
import TimePickerWithInterval from 'src/components/ts-components/TimePickerWithInterval'

/**
 * The date and time controls for the bulk panel AND the grid's cells.
 *
 * These wrap the pickers the rest of the app already uses — antd's `DatePicker`
 * (17 files) and `TimePickerWithInterval` (the Scheduler Wizard's own, 6 files)
 * — rather than anything built for this feature. One control per concept,
 * whether the admin is retiming a hundred games from the toolbar or one from its
 * row, and the same control they meet everywhere else in the product.
 *
 * Both take `getPopupContainer`, and every caller MUST pass one.
 *
 * That prop is the whole reason these wrappers exist. Both pickers open a panel
 * through a PORTAL, which lands in `document.body` by default — outside the
 * popover or cell editor that opened it. A host watching for a click outside
 * itself then reads a click on a day, a month arrow or a time option as a click
 * away, and closes mid-choice. Every "it just auto closes" in this feature was
 * that one bug. Handing back the host's own node keeps the click inside.
 *
 * Values in and out are the canonical civil strings — `YYYY-MM-DD` and `HH:mm` —
 * which is what the venue-local helpers and `planBulkEdit` expect. The pickers
 * speak Dayjs, and that conversion is the only other thing these do.
 */

const CANONICAL_DATE = 'YYYY-MM-DD'
const CANONICAL_TIME = 'HH:mm'

/**
 * Accepted on the way IN, in order. The first is what gets displayed; the rest
 * exist so the shorthand people actually type still parses — `Nov 20`, `11/20`,
 * `Nov 20 2026`. A bare day number is the one shorthand antd cannot take.
 */
const DATE_FORMATS = [
  'YYYY-MM-DD',
  'MMM D, YYYY',
  'MMM D YYYY',
  'MMM D',
  'M/D/YYYY',
  'M/D',
]

export interface PickerProps {
  value: string | null
  onChange: (next: string) => void
  /**
   * Where the panel portals to.
   *
   * antd's own signature, deliberately: the bulk toolbar already has an
   * `insidePopover(triggerNode)` it hands to four other antd fields, and this
   * has to be the same function. A field inside an antd Popover MUST use it —
   * a panel in the body is a click outside the popover, and the popover closes.
   *
   * A grid cell wants the opposite: `document.body`, so antd positions against
   * the page and nothing clips it. Focus containment there is handled by
   * suppressing mousedown on the panel instead.
   */
  getPopupContainer: (triggerNode: HTMLElement) => HTMLElement
  autoFocus?: boolean
  /** Greys the field out — for when another control owns the value. */
  disabled?: boolean
  placeholder?: string
}

/**
 * Wraps antd's calendar so nothing inside it can take focus.
 *
 * Module scope, not an inline arrow: a fresh function here is a fresh component
 * type on every render, which React answers by throwing the panel's DOM away
 * and rebuilding it — the calendar would remount mid-interaction.
 */
const holdFocus = (panel: React.ReactNode) => (
  <div onMouseDown={(event) => event.preventDefault()}>{panel}</div>
)

export const GameDatePicker = ({
  value,
  onChange,
  getPopupContainer,
  autoFocus = false,
}: PickerProps) => {
  'use memo'

  //  Canonical dates are ISO, so no `customParseFormat` plugin is involved.
  const selected = value === null || value === '' ? null : dayjs(value)

  return (
    <AntDatePicker
      autoFocus={autoFocus}
      //  The admin already asked to edit this; making them click the field again
      //  to see a calendar is one click too many.
      defaultOpen={autoFocus}
      allowClear={false}
      format={DATE_FORMATS}
      value={selected !== null && selected.isValid() ? selected : null}
      onChange={(next: Dayjs | null) => {
        //  A cleared or unreadable date keeps the original. A typo must never
        //  silently move a game.
        onChange(next === null ? value ?? '' : next.format(CANONICAL_DATE))
      }}
      suffixIcon={<CalendarOutlined css={suffixStyles} />}
      /*
       * Nothing may take focus from the panel.
       *
       * This is the whole fix for "clicking an arrow closes the editor", and it
       * is `TimePickerWithInterval`'s own trick — its dropdown preventDefaults
       * mousedown, which is exactly why that picker never had the bug. antd's
       * ‹ › and month/year controls are real buttons: without this they take
       * focus, the cell fires `focusout`, and `stopEditingWhenCellsLoseFocus`
       * tears the editor down mid-choice. Clicks still fire; only the focus
       * move is suppressed.
       *
       * Preferred over relocating the panel. Handing antd a container other
       * than the body makes rc-trigger position the panel RELATIVE to that
       * container, and the panel then has to be `position: fixed` to escape the
       * cell's `overflow: hidden` — at which point those container-relative
       * offsets are read as viewport coordinates and the calendar lands in the
       * top-left corner of the page.
       */
      panelRender={holdFocus}
      getPopupContainer={getPopupContainer}
      css={controlStyles}
    />
  )
}

export const GameTimePicker = ({
  value,
  onChange,
  getPopupContainer,
  autoFocus = false,
  disabled = false,
  placeholder,
}: PickerProps) => {
  'use memo'

  /*
   * The wrapper doubles as the trigger node, because `TimePickerWithInterval`
   * portals from a ref this component cannot reach. `insidePopover` only walks
   * UP from whatever it is given, and the wrapper sits in the same popup as the
   * input, so it answers the same.
   *
   * A ref, read lazily: `getPopupContainer` is called when the dropdown OPENS,
   * never during render, so the node is always attached by then. Holding it in
   * state instead would cost a render with no input in it at all, which inside
   * an AG Grid popup editor is long enough for the editor to be torn down.
   */
  const wrapRef = useRef<HTMLDivElement | null>(null)

  //  Focusing the input is what opens the dropdown; without this the cell shows
  //  a field the admin has to click a second time. Skipped while disabled —
  //  there is nothing to open, and the focus would fight the control that owns
  //  the value instead.
  useEffect(() => {
    if (!autoFocus || disabled) {
      return
    }
    wrapRef.current?.querySelector('input')?.focus()
  }, [autoFocus, disabled])

  const selected =
    value === null || value === '' ? null : dayjs(value, CANONICAL_TIME)

  return (
    <div css={timeWrapStyles} ref={wrapRef}>
      <TimePickerWithInterval
        id={autoFocus ? TIME_AUTOFOCUS_ID : undefined}
        disabled={disabled}
        {...(placeholder === undefined ? {} : { placeholder })}
        value={selected !== null && selected.isValid() ? selected : null}
        onChange={(next: Dayjs | null) => {
          onChange(next === null ? value ?? '' : next.format(CANONICAL_TIME))
        }}
        getPopupContainer={() =>
          wrapRef.current === null ? null : getPopupContainer(wrapRef.current)
        }
      />
    </div>
  )
}

/**
 * `TimePickerWithInterval` opens its dropdown from the input's own focus
 * handler and exposes no ref, so focusing the input is the only way to open it
 * from outside. An id is how this reaches it.
 */
const TIME_AUTOFOCUS_ID = 'sn-game-time-editor'

// ─── Styles ──────────────────────────────────────────────────────

/**
 * Matched to `ts-components/DatePicker` — the app's own antd wrapper — so the
 * field reads the same here as it does on the payments and registration screens.
 */
const controlStyles = css`
  ${font.body}
  width: 100%;
  height: 36px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};

  input {
    ${font.body}
    font-size: 14px;
    color: ${colors.NEUTRAL_0};
  }
`

const timeWrapStyles = css`
  ${font.body}
  width: 100%;
`

const suffixStyles = css`
  color: ${colors.NEUTRAL_0};
  font-size: 16px;
`
